import type { AccessPerson } from "@/hooks/use-access";

/**
 * Why a person has no sign-in to show, one rule for Admin > Users (#1017 review): the Platform
 * tab's Sign-in column (`signInLabel`) and the Last active reason (`UsersGrid`) both read it, so
 * one row never says two different things.
 *
 * - `deactivated`: a deactivated person who is not bound to a sign-in. They cannot make one, so
 *   they are not waiting for it (Fixes wave 3).
 * - `awaiting`: invited or pinned, with no sign-in on record yet.
 * - `null`: the person has signed in.
 */
export type SignInWait = "deactivated" | "awaiting" | null;

export function signInWait(
  person: Pick<AccessPerson, "idpIssuer" | "expectedIdpIssuer" | "userStatus" | "lastSignInAt">,
): SignInWait {
  if (person.idpIssuer) return null;
  if (person.userStatus === "disabled") return "deactivated";
  if (person.expectedIdpIssuer || person.userStatus === "pending" || !person.lastSignInAt) {
    return "awaiting";
  }
  return null;
}

/** The words of a wait, as a label: "Deactivated", "Awaiting first sign-in". */
export const SIGN_IN_WAIT_LABEL: Record<Exclude<SignInWait, null>, string> = {
  deactivated: "Deactivated",
  awaiting: "Awaiting first sign-in",
};
